/**
 * @auth 赖传峰
 * @date 2025/2/12
 */

import { computed, ref, useCookie, type WritableComputedRef } from '#imports'
import type { Ref, WritableComputedOptions } from 'vue'
import { useRoute } from 'vue-router'
import { isValidViewType } from '~/components/share/share.script'
import DisplayMode, { type DisplayModeStruct, type SharedDisplayModeStruct } from '~/composables/DisplayMode'
import { type OrderType } from '~/store/share.script'
import { COOKIE_OPTIONS } from '~/types/constants'
import { RouteQueryKey, StorageKey, ViewType } from '~/types/enum'

const changeManual = ref(false)

export default (): {
  orderType: WritableComputedRef<OrderType>
  pinFoldersTop: WritableComputedRef<boolean>
  viewType: Ref<ViewType>
} => {
  const route = useRoute()
  const globalDisplayMode = useCookie<DisplayModeStruct>(StorageKey.GLOBAL_DISPLAY_MODE, COOKIE_OPTIONS)
  const sharedDisplayMode = useCookie<SharedDisplayModeStruct>(StorageKey.SHARED_DISPLAY_MODE, COOKIE_OPTIONS)
  /** 初始化 Cookie，确保有值，Server 环境，只有这个执行 */
  globalDisplayMode.value ||= DisplayMode.DEFAULT_DISPLAY_MODE
  sharedDisplayMode.value ||= DisplayMode.SHARED_DEFAULT_DISPLAY_MODE
  // NOTE: 设置 sameSite 时，会导致跨站跳转时获取不到
  // 会导致显示的视图与用户手动选择的视图不一致
  const ssrViewType = useCookie<ViewType>(StorageKey.SSR_VIEW_TYPE, { secure: true })

  DisplayMode.initWatch()

  const writeableComputedOptions = <T extends keyof SharedDisplayModeStruct>(
    key: T,
  ): WritableComputedOptions<SharedDisplayModeStruct[T]> => ({
    get() {
      // NOTE: 处理手动删除 Cookie 的情况, Cookie 的值会变为 null
      if (globalDisplayMode.value === null || sharedDisplayMode.value === null) {
        globalDisplayMode.value = DisplayMode.DEFAULT_DISPLAY_MODE
        sharedDisplayMode.value = DisplayMode.SHARED_DEFAULT_DISPLAY_MODE
      }

      return (globalDisplayMode.value.orderSettingGlobal ? globalDisplayMode : sharedDisplayMode).value[key]
    },
    set(val: SharedDisplayModeStruct[T]) {
      ;(globalDisplayMode.value.orderSettingGlobal ? globalDisplayMode : sharedDisplayMode).value[key] = val
    },
  })

  const orderType = computed<OrderType>(writeableComputedOptions('orderType'))
  const pinFoldersTop = computed<boolean>(writeableComputedOptions('pinFoldersTop'))
  const viewType = computed<ViewType>({
    get() {
      // NOTE: query 的优先级最高, 直到用户手动切换
      if (!changeManual.value && route.query[RouteQueryKey.VIEW_TYPE]) {
        const newViewType = route.query[RouteQueryKey.VIEW_TYPE] as string

        if (isValidViewType(newViewType)) {
          if (ssrViewType.value === undefined || ssrViewType.value !== newViewType) {
            ssrViewType.value = newViewType
          }
          return newViewType
        }
      }
      // NOTE: 使用基本类型, 防止修改
      if (ssrViewType.value) {
        return ssrViewType.value
      }
      // NOTE: 如果获取不到有两个可能, 1 是没有这个值, 2 是没有读取的权限
      // 所以设置一个默认值, 来覆盖 1、2 场景
      // 避免水合失败问题
      ssrViewType.value = DisplayMode.DEFAULT_DISPLAY_MODE.viewType
      return DisplayMode.DEFAULT_DISPLAY_MODE.viewType
    },
    set(val) {
      // NOTE: 确保不会在 Server 执行
      if (import.meta.client) {
        changeManual.value = true
        // NOTE: 同步至 PikPak Web 侧的共享 Cookie
        if (globalDisplayMode.value) {
          globalDisplayMode.value.viewType = val
        }
      }
      ssrViewType.value = val
    },
  })

  return { orderType, pinFoldersTop, viewType }
}
